import type { Params, Path } from 'react-router';

import { ZodObject, ZodRawShape, ZodTypeAny, output, z } from 'zod';

import { localeForChannel } from '../channels/locale';
import { IChannel } from '../channels/types';
import { IImgUrlOptions, MimeType } from './types';

const urlFunctionsFactory = ({
  channels,
  baseURL,
  locales,
  baseCanonicalUrl,
}: {
  channels: { [key: string]: IChannel };
  baseURL: string;
  locales: string[];
  baseCanonicalUrl?: string;
}) => {
  const schLocale = z.string().refine((locale) => locales.includes(locale));
  const schChannel = z.string().refine((channel) => channel in channels);

  const schemaParams = z
    .object({
      locale: schLocale,
      channel: schChannel,
    })
    .transform((data) => {
      return {
        locale: data.locale,
        channel: channels[data.channel],
        saleorLocale: localeForChannel(channels[data.channel], data.locale),
      };
    });

  const imgUrl = (src: string, options: IImgUrlOptions) => {
    const url = new URL('/api/image/', baseURL);

    url.searchParams.set('src', src);

    url.searchParams.set('width', options.width.toString());

    options?.height && url.searchParams.set('height', options.height.toString());
    options?.quality && url.searchParams.set('quality', options.quality.toString());
    options?.fit && url.searchParams.set('fit', options.fit);
    options?.position && url.searchParams.set('position', options.position);

    url.searchParams.set('contentType', options?.contentType || MimeType.WEBP);

    return `${url.pathname}${url.search}`;
  };

  const absoluteify = (path: string) => {
    const url = new URL(path, baseURL);

    // Only add trailing slash if URL doesn't have a file extension
    const hasExtension = /\.[a-zA-Z0-9]+$/.test(url.pathname);
    if (!hasExtension && !url.pathname.endsWith('/')) {
      url.pathname += '/';
    }

    return url.href;
  };

  const canonicalize = (location: Path, allowedKeys?: string[]) => {
    const path = location.pathname;

    const url = new URL(path, baseCanonicalUrl || baseURL);
    const qs = new URLSearchParams(location.search);
    const qsCleaned = new URLSearchParams();

    for (const [key, value] of qs) {
      if ((allowedKeys || []).includes(key)) {
        qsCleaned.set(key, value);
      }
    }

    url.search = qsCleaned.toString();

    // Only add trailing slash if URL doesn't have a file extension
    const hasExtension = /\.[a-zA-Z0-9]+$/.test(url.pathname);
    if (!hasExtension && !url.pathname.endsWith('/')) {
      url.pathname += '/';
    }

    return url.href;
  };

  function withLocaleChannel(generator: (locale: string, channel: IChannel) => string) {
    return (params: Params, replacements?: { locale?: string; channel?: IChannel }) => {
      const { locale: localeFromParams, channel: channelFromParams } = schemaParams.parse(params);

      let localeToUse = replacements?.locale || localeFromParams;
      let channelToUse = replacements?.channel || channelFromParams;

      return generator(localeToUse, channelToUse);
    };
  }

  function withLocaleChannelWithVars<TVars>(
    generator: (locale: string, channel: IChannel, vars: TVars) => string
  ) {
    return (
      params: Params,
      vars: TVars,
      replacements?: { locale?: string; channel?: IChannel }
    ) => {
      const { locale: localeFromParams, channel: channelFromParams } = schemaParams.parse(params);

      let localeToUse = replacements?.locale || localeFromParams;
      let channelToUse = replacements?.channel || channelFromParams;

      return generator(localeToUse, channelToUse, vars);
    };
  }

  function parseParams(params: Params) {
    try {
      return schemaParams.parse(params);
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new Response('Not Found', {
          status: 404,
          statusText: 'Invalid locale or region',
        });
      } else {
        throw error;
      }
    }
  }

  return {
    imgUrl,
    parseParams,
    schLocaleChannel: schemaParams,
    schLocale,
    schChannel,
    absoluteify,
    canonicalize,
    withLocaleChannel,
    withLocaleChannelWithVars,
  };
};

export type TImgUrl = ReturnType<typeof urlFunctionsFactory>['imgUrl'];

export default urlFunctionsFactory;

export function parseRouteParams<T extends ZodRawShape | ZodTypeAny>(
  params: Params,
  schema: T
): ParsedData<T> {
  try {
    const finalSchema = isZodType(schema) ? schema : z.object(schema);

    return finalSchema.parse(params);
  } catch (_e) {
    throw new Response('Not Found', {
      status: 404,
    });
  }
}

const isZodType = (input: ZodRawShape | ZodTypeAny): input is ZodTypeAny => {
  return typeof input.parse === 'function';
};

type ParsedData<T extends ZodRawShape | ZodTypeAny> = T extends ZodTypeAny
  ? output<T>
  : T extends ZodRawShape
    ? output<ZodObject<T>>
    : never;
